<template>
  <div>
    <DetailTemplate
      :customClass="'purchase-order-detail detail-page'"
      v-if="getPermission('purchase-order:view')"
    >
      <template v-slot:sidebar-detail>
        <v-card class="h-100" elevation="0">
          <div class="d-flex">
            <template v-if="pageLoading">
              <v-skeleton-loader
                class="custom-skeleton height-30px my-auto mx-2 custom-skeleton-full-width"
                width="130"
                type="text"
              >
              </v-skeleton-loader>
            </template>
            <template v-else>
              <v-chip
                class="mr-2 my-1 custom-status font-weight-600 custom-grey-border text-uppercase justify-center font-10"
                :color="getStatusColor(detail.status)"
                label
                :text-color="detail.status == 2 ? 'white' : 'white'"
              >
                {{ detail.status_text }}
              </v-chip>
              <v-chip
                v-if="isApprovedPO && detail.payment_status"
                class="mr-2 my-1 custom-status font-weight-600 custom-grey-border text-uppercase justify-center font-10"
                :color="getPaymentStatusColor(detail.payment_status)"
                label
                text-color="white"
              >
                {{ detail.payment_status_text }}
              </v-chip>
            </template>
          </div>
          <div class="mt-5">
            <template v-if="pageLoading">
              <v-skeleton-loader
                v-for="(d, i) in 4"
                :key="i"
                class="custom-skeleton height-40px custom-skeleton-full-width mr-5"
                :class="{ 'mt-3': i <= 0, 'my-1': i > 0 }"
                type="text"
              >
              </v-skeleton-loader>
            </template>
            <template v-else>
              <table class="width-100">
                <tr>
                  <th
                    colspan="2"
                    class="grey lighten-4 font-size-16 color-custom-blue py-2 px-3 mb-2 text-uppercase"
                  >
                    Basic Information
                  </th>
                </tr>
                <tr v-if="detail.project">
                  <td class="font-size-18 font-weight-500 py-1" width="200">
                    Project #
                  </td>
                  <td class="font-size-18 py-1">
                    {{ detail.project.barcode }} -
                    {{ detail.project.name }}
                  </td>
                </tr>
                <tr>
                  <td class="font-size-18 font-weight-500 py-1" width="200">
                    Purchase Order #
                  </td>
                  <td class="font-size-18 py-1 custom-nowrap-ellipsis">
                    <Barcode :barcode="detail.barcode"></Barcode>
                  </td>
                </tr>
                <tr>
                  <td
                    class="font-size-18 font-weight-500 py-1 border-bottom-dashed"
                    width="160"
                  >
                    Reference #
                  </td>
                  <td
                    class="font-size-18 py-1 border-bottom-dashed custom-nowrap-ellipsis"
                  >
                    <template v-if="detail.reference_no">{{
                      detail.reference_no
                    }}</template>
                    <template v-else
                      ><em class="text--secondary font-weight-500"
                        >No Reference #</em
                      ></template
                    >
                  </td>
                </tr>
                <tr>
                  <td class="font-size-18 font-weight-500 py-1">
                    Payment Term
                  </td>
                  <td class="font-size-18 py-1 custom-nowrap-ellipsis">
                    <template v-if="detail.payment_term"
                      ><v-icon small color="primary" class="mr-1">
                        mdi-credit-card-outline </v-icon
                      >{{ detail.payment_term.text }}</template
                    >
                    <template v-else
                      ><em class="text--secondary font-weight-500"
                        >No Payment Term</em
                      ></template
                    >
                  </td>
                </tr>
                <tr>
                  <td class="font-size-18 font-weight-500 py-1">
                    Purchase date
                  </td>
                  <td class="font-size-18 py-1 pr-4 custom-border-right">
                    <template v-if="detail.date">
                      <v-icon small color="primary" class="mr-1">
                        mdi-calendar-outline </v-icon
                      >{{ formatDate(detail.date) }}
                    </template>
                    <template v-else
                      ><em class="text--secondary font-weight-500"
                        >no Purchase date</em
                      ></template
                    >
                  </td>
                </tr>
                <tr>
                  <td class="font-size-18 font-weight-500 py-1">
                    Expected delivery
                  </td>
                  <td width="300" class="font-size-18 py-1">
                    <template v-if="detail.delivery_date">
                      <v-icon small color="orange" class="mr-1">
                        mdi-truck-delivery-outline </v-icon
                      >{{ formatDate(detail.delivery_date) }}</template
                    >
                    <template v-else
                      ><em class="text--secondary font-weight-500"
                        >no Expected delivery date</em
                      ></template
                    >
                  </td>
                </tr>
                <tr v-if="detail.due_date">
                  <td class="font-size-18 font-weight-500 py-1">Due date</td>
                  <td class="font-size-18 py-1">
                    <template v-if="detail.due_date"
                      ><v-icon small color="red" class="mr-1">
                        mdi-calendar-alert-outline </v-icon
                      >{{ formatDate(detail.due_date) }}</template
                    >
                    <template v-else
                      ><em class="text--secondary font-weight-500"
                        >no due date</em
                      ></template
                    >
                  </td>
                </tr>
                <tr>
                  <td class="font-size-18 font-weight-500 py-1">Category</td>
                  <td class="font-size-18 py-1">
                    <template v-if="detail.category">{{
                      detail.category
                    }}</template>
                    <template v-else
                      ><em class="text--secondary font-weight-500"
                        >no category</em
                      ></template
                    >
                  </td>
                </tr>
                <tr>
                  <td class="font-size-18 font-weight-500 py-1">PIC</td>
                  <td class="font-size-18 py-1 custom-nowrap-ellipsis">
                    <template
                      v-if="detail.pic_user && detail.pic_user.display_name"
                      ><v-icon small color="primary" class="mr-1">
                        mdi-account-outline </v-icon
                      >{{ detail.pic_user.display_name }}</template
                    >
                    <template v-else
                      ><em class="text--secondary font-weight-500"
                        >No PIC</em
                      ></template
                    >
                  </td>
                </tr>
                <tr>
                  <td class="font-size-18 font-weight-500 py-1">Order By</td>
                  <td class="font-size-18 py-1 custom-nowrap-ellipsis">
                    <template
                      v-if="
                        detail.order_by_user &&
                        detail.order_by_user.display_name
                      "
                    >
                      <v-icon small color="primary" class="mr-1">
                        mdi-account-check-outline </v-icon
                      >{{ detail.order_by_user.display_name }}</template
                    >
                    <template v-else
                      ><em class="text--secondary font-weight-500"
                        >No Order By</em
                      ></template
                    >
                  </td>
                </tr>
              </table>
            </template>
          </div>
        </v-card>
      </template>
      <template v-slot:header-title>
        <div class="d-flex">
          <template v-if="pageLoading">
            <v-skeleton-loader
              class="custom-skeleton"
              width="400"
              height="28"
              type="text"
            >
            </v-skeleton-loader>
          </template>
          <template v-else>
            <h1 class="form-title custom-nowrap-ellipsis text-capitalize mr-4">
              {{ detail.title }}
            </h1>
            <!-- <CustomStatus
            :status="detail.status"
            endpoint="purchase-order/status"
          ></CustomStatus> -->
          </template>
        </div>
      </template>
      <template v-slot:header-action>
        <v-btn
          depressed
          v-if="canReceiveLineItems"
          :disabled="pageLoading"
          class="mx-2 custom-bold-button white--text"
          color="green"
          v-on:click="receiveLineItemDialog = true"
        >
          <v-icon small left>mdi-package-down</v-icon>
          Receive Line Items
        </v-btn>
        <v-menu
          transition="slide-y-transition"
          bottom
          content-class="custom-menu-list"
          offset-y
          left
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              depressed
              :disabled="!formValid || formLoading || pageLoading"
              :loading="formLoading"
              class="mx-2 custom-bold-button white--text"
              color="cyan"
              v-bind="attrs"
              v-on="on"
            >
              More... <v-icon right>mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list>
            <template v-for="(more, index) in moreActions">
              <v-list-item
                link
                v-on:click="updateMoreAction(more.action)"
                :key="index"
                :disabled="more.disabled"
                v-if="more.action !== 'mark_as_approved' || isPurchaseOrderPic"
              >
                <v-list-item-icon class="margin-auto-zero mr-3 my-2">
                  <v-icon class="icon-default-blue">{{ more.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="font-weight-500 font-size-14">{{
                  more.title
                }}</v-list-item-title>
              </v-list-item>
            </template>
          </v-list>
        </v-menu>
        <v-btn
          depressed
          class="mx-2 custom-grey-border custom-bold-button"
          v-on:click="goBack"
        >
          <v-icon small left>mdi-keyboard-backspace</v-icon>
          Back
        </v-btn>
      </template>
      <template v-slot:body>
        <v-container fluid clas="pt-0">
          <v-row dense>
            <v-col cols="5" class="pb-0" v-if="false">
              <v-container fluid>
                <v-list flat>
                  <v-skeleton-loader
                    class="custom-skeleton height-40px custom-skeleton-full-width"
                    type="text"
                    v-if="pageLoading"
                  >
                  </v-skeleton-loader>
                  <template v-else>
                    <v-tooltip top content-class="custom-top-tooltip">
                      <template v-slot:activator="{ on, attrs }">
                        <v-list-item
                          class="py-1 max-content-width"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-list-item-icon class="margin-auto-zero mr-4">
                            <v-icon class="font-size-28">mdi-account</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content class="p-0">
                            <v-list-item-title
                              class="custom-nowrap-ellipsis text-capitalize font-size-18 font-weight-500"
                            >
                              <router-link
                                :to="
                                  getDefaultRoute('supplier.detail', {
                                    params: {
                                      id: detail.supplier
                                        ? detail.supplier.id
                                        : 0,
                                    },
                                  })
                                "
                              >
                                <!-- {{
                                  detail.supplier
                                    ? detail.supplier.full_name
                                    : ""
                                }} -->
                                full Name
                              </router-link>
                            </v-list-item-title>
                          </v-list-item-content>
                          <v-list-item-action
                            link
                            class="m-0 ml-4"
                            v-on:click="
                              $router.push(
                                getDefaultRoute('supplier.detail', {
                                  params: {
                                    id: detail.supplier
                                      ? detail.supplier.id
                                      : 0,
                                  },
                                })
                              )
                            "
                          >
                            <v-icon class="color-custom-blue"
                              >mdi-link mdi-rotate-135</v-icon
                            >
                          </v-list-item-action>
                        </v-list-item>
                      </template>
                      <span>Attention</span>
                    </v-tooltip>
                  </template>
                  <v-skeleton-loader
                    class="custom-skeleton height-40px custom-skeleton-full-width"
                    type="text"
                    v-if="pageLoading"
                  >
                  </v-skeleton-loader>
                  <template v-else>
                    <v-tooltip top content-class="custom-top-tooltip">
                      <template v-slot:activator="{ on, attrs }">
                        <v-list-item
                          class="py-1 max-content-width"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-list-item-icon class="margin-auto-zero mr-4">
                            <v-icon class="font-size-28"
                              >mdi-office-building-outline</v-icon
                            >
                          </v-list-item-icon>
                          <v-list-item-content class="p-0">
                            <v-list-item-title
                              class="custom-nowrap-ellipsis text-capitalize font-size-18 font-weight-500"
                            >
                              <router-link
                                :to="
                                  getDefaultRoute('supplier.detail', {
                                    params: {
                                      id: detail.supplier
                                        ? detail.supplier.id
                                        : 0,
                                    },
                                  })
                                "
                              >
                                <!-- {{
                                  detail.supplier
                                    ? detail.supplier.company_name
                                    : ""
                                }} -->
                                Company Name
                              </router-link>
                            </v-list-item-title>
                          </v-list-item-content>
                          <v-list-item-action
                            link
                            class="m-0 ml-4"
                            v-on:click="
                              $router.push(
                                getDefaultRoute('supplier.detail', {
                                  params: {
                                    id: detail.supplier
                                      ? detail.supplier.id
                                      : 0,
                                  },
                                })
                              )
                            "
                          >
                            <v-icon class="color-custom-blue"
                              >mdi-link mdi-rotate-135</v-icon
                            >
                          </v-list-item-action>
                        </v-list-item>
                      </template>
                      <span>Company Name</span>
                    </v-tooltip>
                  </template>
                  <v-skeleton-loader
                    class="custom-skeleton height-40px custom-skeleton-full-width"
                    type="text"
                    v-if="pageLoading"
                  >
                  </v-skeleton-loader>
                  <template v-else>
                    <v-tooltip top content-class="custom-top-tooltip">
                      <template v-slot:activator="{ on, attrs }">
                        <v-list-item
                          class="py-1 max-content-width"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-list-item-icon class="margin-auto-zero mr-4">
                            <v-icon class="font-size-28">mdi-email</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content class="p-0">
                            <v-list-item-title
                              class="custom-nowrap-ellipsis text-lowercase font-size-18 font-weight-500"
                            >
                              <a :href="'mailto:' + supplierEmail">
                                john@gmail.com
                                <!-- {{
                                supplierEmail
                              }} -->
                              </a>
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                      <span>Contact Email</span>
                    </v-tooltip>
                  </template>
                  <v-skeleton-loader
                    class="custom-skeleton height-40px custom-skeleton-full-width"
                    type="text"
                    v-if="pageLoading"
                  >
                  </v-skeleton-loader>
                  <template v-else>
                    <v-tooltip top content-class="custom-top-tooltip">
                      <template v-slot:activator="{ on, attrs }">
                        <v-list-item
                          class="py-1 max-content-width"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-list-item-icon class="margin-auto-zero mr-4">
                            <v-icon class="font-size-28">mdi-phone</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content class="p-0">
                            <v-list-item-title
                              class="custom-nowrap-ellipsis text-lowercase font-size-18 font-weight-500"
                            >
                              <a :href="'tel:' + supplierPhone">
                                +65 1234 5678
                                <!-- {{
                                supplierPhone
                              }} -->
                              </a>
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                      <span>Contact Phone</span>
                    </v-tooltip>
                  </template>
                  <v-skeleton-loader
                    class="custom-skeleton height-40px custom-skeleton-full-width"
                    type="text"
                    v-if="pageLoading"
                  >
                  </v-skeleton-loader>
                  <template v-else>
                    <v-tooltip top content-class="custom-top-tooltip">
                      <template v-slot:activator="{ on, attrs }">
                        <v-list-item class="py-1" v-bind="attrs" v-on="on">
                          <v-list-item-icon class="margin-auto-zero mr-4">
                            <v-icon class="font-size-28"
                              >mdi-home-map-marker</v-icon
                            >
                          </v-list-item-icon>
                          <v-list-item-content class="p-0">
                            <v-list-item-title
                              class="custom-nowrap-ellipsis-two-line font-size-18 font-weight-500"
                            >
                              <a
                                target="_blank"
                                :href="
                                  'https://maps.google.com/?q=' +
                                  supplierAddress
                                "
                              >
                                singapore
                                <!-- {{ supplierAddress }} -->
                              </a>
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                      <span>Supplier Address</span>
                    </v-tooltip>
                  </template>
                </v-list>
              </v-container>
            </v-col>
            <v-col cols="7" class="pb-0 gray-background pl-4" v-if="false">
              <v-container fluid class="pt-0">
                <v-row dense>
                  <v-col md="12">
                    <template v-if="pageLoading">
                      <v-skeleton-loader
                        v-for="(d, i) in 4"
                        :key="i"
                        class="custom-skeleton height-40px custom-skeleton-full-width mr-5"
                        :class="{ 'mt-3': i <= 0, 'my-1': i > 0 }"
                        type="text"
                      >
                      </v-skeleton-loader>
                    </template>
                    <template v-else>
                      <table class="width-100">
                        <tr>
                          <td class="font-size-18 pt-3 pb-1" width="200">
                            Purchase Order #
                          </td>
                          <td class="font-weight-600 font-size-18 pt-3 pb-1">
                            {{ detail.barcode }}
                          </td>
                        </tr>
                        <tr v-if="detail.project">
                          <td class="font-size-18 py-1" width="200">
                            Project #
                          </td>
                          <td class="font-weight-600 font-size-18 py-1">
                            {{ detail.project.barcode }} -
                            {{ detail.project.name }}
                          </td>
                        </tr>
                        <tr>
                          <td class="font-size-18 py-1" width="200">
                            Reference #
                          </td>
                          <td
                            class="font-weight-600 font-size-18 py-1 custom-nowrap-ellipsis"
                          >
                            <template v-if="detail.reference">{{
                              detail.reference
                            }}</template>
                            <template v-else
                              ><em class="text--secondary font-weight-500"
                                >No Reference #</em
                              ></template
                            >
                          </td>
                        </tr>
                        <tr>
                          <td class="font-size-18 py-1" width="200">
                            Supplier Ref #
                          </td>
                          <td
                            class="font-weight-600 font-size-18 py-1 custom-nowrap-ellipsis"
                          >
                            <template v-if="detail.reference">{{
                              detail.supplier_reference
                            }}</template>
                            <template v-else
                              ><em class="text--secondary font-weight-500"
                                >No Supplier Ref #</em
                              ></template
                            >
                          </td>
                        </tr>
                        <tr
                          v-if="lodash.isEmpty(detail.payment_term) === false"
                        >
                          <td class="font-size-18 py-1" width="200">
                            Payment Term
                          </td>
                          <td
                            class="font-weight-600 font-size-18 py-1 custom-nowrap-ellipsis"
                          >
                            <template v-if="detail.payment_term">{{
                              detail.payment_term.text
                            }}</template>
                            <template v-else
                              ><em class="text--secondary font-weight-500"
                                >No Payment Term</em
                              ></template
                            >
                          </td>
                        </tr>
                        <tr class="custom-border-top">
                          <td
                            class="font-size-18 py-1 custom-border-right pr-4"
                            width="200"
                          >
                            Purchase order date
                          </td>
                          <td
                            class="font-size-18 px-4 py-1 custom-border-right"
                            width="200"
                          >
                            Expected delivery date
                          </td>
                          <td class="font-size-18 px-4 py-1" width="200">
                            Due date
                          </td>
                        </tr>
                        <tr>
                          <td
                            width="200"
                            class="font-weight-600 font-size-18 py-1 pr-4 custom-border-right"
                          >
                            {{ formatDate(detail.date) }}
                          </td>
                          <td
                            width="300"
                            class="font-weight-600 font-size-18 py-1 px-4 custom-border-right"
                          >
                            <template v-if="detail.expected_delivery_date">{{
                              formatDate(detail.expected_delivery_date)
                            }}</template>
                            <template v-else
                              ><em class="text--secondary font-weight-500"
                                >no Expected delivery date</em
                              ></template
                            >
                          </td>
                          <td
                            width="200"
                            class="font-weight-600 font-size-18 py-1 px-4"
                          >
                            <template v-if="detail.due_date">{{
                              formatDate(detail.due_date)
                            }}</template>
                            <template v-else
                              ><em class="text--secondary font-weight-500"
                                >no due date</em
                              ></template
                            >
                          </td>
                        </tr>
                      </table>
                    </template>
                  </v-col>
                </v-row>
              </v-container>
            </v-col>
            <v-col cols="12" class="px-0 py-0">
              <v-tabs
                active-class="custom-tab-active"
                v-model="purchaseOrderTab"
                background-color="transparent"
                color="cyan"
                class="custom-tab-transparent tab-sticky"
              >
                <v-tab
                  :disabled="pageLoading"
                  class="font-size-16 font-weight-600 px-8"
                  href="#overview"
                >
                  <span class="svg-icon svg-icon-lg mr-4 svg-v-list-icon">
                    <!--begin::Svg Icon-->
                    <inline-svg
                      :src="$assetURL('media/custom-svg/purchase-order.svg')"
                    />
                    <!--end::Svg Icon-->
                  </span>
                  Overview
                </v-tab>
                <v-tab
                  :disabled="pageLoading"
                  class="font-size-16 font-weight-600 px-8"
                  href="#line-item"
                >
                  <span class="svg-icon svg-icon-lg mr-4 svg-v-list-icon">
                    <!--begin::Svg Icon-->
                    <inline-svg
                      :src="$assetURL('media/custom-svg/line-item.svg')"
                    />
                    <!--end::Svg Icon-->
                  </span>
                  <v-badge
                    v-if="detail.line_items_count"
                    bordered
                    color="orange darken-4"
                    :content="detail.line_items_count"
                  >
                    {{
                      detail.line_items_count > 1 ? "Line Item" : "Line Item"
                    }}
                  </v-badge>
                  <template v-else> Line Item </template>
                </v-tab>
                <v-tab
                  :disabled="pageLoading"
                  class="font-size-16 font-weight-600 px-8"
                  href="#attachment"
                >
                  <span class="svg-icon svg-icon-lg mr-4 svg-v-list-icon">
                    <!--begin::Svg Icon-->
                    <inline-svg
                      :src="$assetURL('media/custom-svg/attachment.svg')"
                    />
                    <!--end::Svg Icon-->
                  </span>
                  Attachments
                </v-tab>
                <v-tab
                  :disabled="pageLoading"
                  class="font-size-16 font-weight-600 px-8"
                  href="#received-line-item"
                >
                  <span class="svg-icon svg-icon-lg mr-4 svg-v-list-icon">
                    <!--begin::Svg Icon-->
                    <inline-svg
                      :src="$assetURL('media/custom-svg/line-item.svg')"
                    />
                    <!--end::Svg Icon-->
                  </span>
                  <v-badge
                    v-if="detail.line_items_count"
                    bordered
                    color="orange darken-4"
                    :content="detail.line_items_count"
                  >
                    {{
                      detail.line_items_count > 1 ? "Line Item" : "Line Item"
                    }}
                  </v-badge>
                  <template v-else> Received Line Item </template>
                </v-tab>
                <v-tab
                  :disabled="pageLoading"
                  class="font-size-16 font-weight-600 px-8"
                  href="#invoice"
                >
                  <span class="svg-icon svg-icon-lg mr-4 svg-v-list-icon">
                    <!--begin::Svg Icon-->
                    <inline-svg
                      :src="$assetURL('media/custom-svg/invoice.svg')"
                    />
                    <!--end::Svg Icon-->
                  </span>
                  Invoice
                </v-tab>
                <v-tab
                  :disabled="pageLoading"
                  class="font-size-16 font-weight-600 px-8"
                  href="#history"
                >
                  <span class="svg-icon svg-icon-lg mr-4 svg-v-list-icon">
                    <!--begin::Svg Icon-->
                    <inline-svg
                      :src="$assetURL('media/custom-svg/history.svg')"
                    />
                    <!--end::Svg Icon-->
                  </span>
                  History
                </v-tab>
              </v-tabs>
              <v-tabs-items v-model="purchaseOrderTab">
                <v-tab-item value="overview">
                  <perfect-scrollbar
                    :options="{ suppressScrollX: true }"
                    class="scroll"
                    style="height: calc(100vh - 155px); position: relative"
                  >
                    <v-row>
                      <v-col cols="12">
                        <v-card outlined elevation="0" class="mb-4">
                          <div
                            class="grey lighten-5 d-flex align-center justify-space-between px-4 py-3 border-bottom"
                          >
                            <div class="d-flex align-center">
                              <v-icon color="cyan darken-2" small class="mr-2"
                                >mdi-office-building</v-icon
                              >
                              <h3
                                class="font-weight-700 color-custom-blue font-size-18 mb-0 text-uppercase"
                              >
                                Supplier Overview
                              </h3>
                            </div>
                          </div>
                          <v-card-text class="px-4 py-1 font-size-16">
                            <table class="width-100">
                              <tbody>
                                <!-- Attention -->
                                <tr>
                                  <td
                                    class="font-size-18 font-weight-500 py-2 border-bottom-dashed"
                                    style="width: 170px"
                                  >
                                    Attention
                                  </td>

                                  <td
                                    class="font-weight-medium py-2 border-bottom-dashed"
                                  >
                                    <v-skeleton-loader
                                      v-if="pageLoading"
                                      type="text"
                                      width="180"
                                    />

                                    <router-link
                                      v-else
                                      :to="
                                        getDefaultRoute('supplier.detail', {
                                          params: {
                                            id: detail.supplier
                                              ? detail.supplier.id
                                              : 0,
                                          },
                                        })
                                      "
                                      class="color-custom-blue text-decoration-none d-inline-flex align-center font-weight-600"
                                    >
                                      <span>
                                        {{
                                          detail.supplier
                                            ? detail.supplier.display_name
                                            : ""
                                        }}
                                      </span>

                                      <v-icon
                                        small
                                        color="cyan darken-2"
                                        class="ml-1"
                                      >
                                        mdi-open-in-new
                                      </v-icon>
                                    </router-link>
                                  </td>
                                </tr>

                                <!-- Company Name -->
                                <tr>
                                  <td
                                    class="font-size-18 font-weight-500 py-2 border-bottom-dashed"
                                    style="width: 170px"
                                  >
                                    Company Name
                                  </td>

                                  <td
                                    class="font-weight-medium py-2 border-bottom-dashed"
                                  >
                                    <v-skeleton-loader
                                      v-if="pageLoading"
                                      type="text"
                                      width="180"
                                    />

                                    <router-link
                                      v-else
                                      :to="
                                        getDefaultRoute('supplier.detail', {
                                          params: {
                                            id: detail.supplier
                                              ? detail.supplier.id
                                              : 0,
                                          },
                                        })
                                      "
                                      class="color-custom-blue text-decoration-none d-inline-flex align-center font-weight-600"
                                    >
                                      <span>
                                        {{
                                          detail.supplier
                                            ? detail.supplier.company_name
                                            : ""
                                        }}
                                      </span>

                                      <v-icon
                                        small
                                        color="blue darken-2"
                                        class="ml-1"
                                      >
                                        mdi-open-in-new
                                      </v-icon>
                                    </router-link>
                                  </td>
                                </tr>

                                <!-- Contact Email -->
                                <tr>
                                  <td
                                    class="font-size-18 font-weight-500 py-2 border-bottom-dashed"
                                    style="width: 170px"
                                  >
                                    Contact Email
                                  </td>

                                  <td
                                    class="font-weight-medium py-2 border-bottom-dashed"
                                  >
                                    <a
                                      v-if="
                                        detail &&
                                        detail.supplier &&
                                        detail.supplier.company_email
                                      "
                                      :href="
                                        'mailto:' +
                                        detail.supplier.company_email
                                      "
                                      class="cyan--text text--darken-3 text-decoration-none d-inline-flex align-center font-weight-600"
                                    >
                                      {{ detail.supplier.company_email }}

                                      <v-icon
                                        small
                                        color="teal darken-2"
                                        class="ml-1"
                                      >
                                        mdi-email-send-outline
                                      </v-icon>
                                    </a>

                                    <em v-else class="grey--text">
                                      No Email
                                    </em>
                                  </td>
                                </tr>

                                <!-- Contact Phone -->
                                <tr>
                                  <td
                                    class="font-size-18 font-weight-500 py-2 border-bottom-dashed"
                                    style="width: 170px"
                                  >
                                    Contact Phone
                                  </td>

                                  <td
                                    class="font-weight-medium py-2 border-bottom-dashed"
                                  >
                                    <a
                                      v-if="
                                        detail &&
                                        detail.supplier &&
                                        detail.supplier.company_number
                                      "
                                      :href="
                                        'tel:' + detail.supplier.company_number
                                      "
                                      class="cyan--text text--darken-3 text-decoration-none d-inline-flex align-center font-weight-600"
                                    >
                                      {{ detail.supplier.company_number }}

                                      <v-icon
                                        small
                                        color="amber darken-3"
                                        class="ml-1"
                                      >
                                        mdi-phone-outgoing
                                      </v-icon>
                                    </a>

                                    <em v-else class="grey--text">
                                      No Phone
                                    </em>
                                  </td>
                                </tr>

                                <!-- Supplier Address -->
                                <tr>
                                  <td
                                    class="font-size-18 font-weight-500 py-2"
                                    style="width: 170px"
                                  >
                                    Supplier Address
                                  </td>

                                  <td class="font-weight-medium">
                                    <a
                                      v-if="
                                        detail.supplier &&
                                        detail.supplier.address &&
                                        detail.supplier.address[0]
                                      "
                                      target="_blank"
                                      :href="
                                        'https://maps.google.com/?q=' +
                                        detail.supplier.address[0]
                                          .property_address
                                      "
                                      class="cyan--text text--darken-3 text-decoration-none d-inline-flex align-center"
                                    >
                                      {{
                                        detail.supplier.address[0]
                                          .property_address
                                      }}

                                      <v-icon
                                        small
                                        color="purple darken-2"
                                        class="ml-1"
                                      >
                                        mdi-open-in-new
                                      </v-icon>
                                    </a>

                                    <em v-else class="grey--text">
                                      No Address
                                    </em>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </v-card-text>
                        </v-card>
                        <!-- <LineItemOnlyView
                    isPurchaseOrder
                    type="purchase_order"
                    :detail="detail"
                    :pageLoading="pageLoading"
                  ></LineItemOnlyView> -->
                      </v-col>
                      <v-col cols="12">
                        <v-card
                          flat
                          class="custom-grey-border remove-border-radius mb-4"
                        >
                          <v-card-title
                            class="border-bottom gray-background py-1"
                          >
                            <span
                              class="font-weight-700 custom-headline color-custom-blue font-size-16"
                            >
                              DESCRIPTION
                            </span>
                          </v-card-title>
                          <v-card-text class="p-6 font-size-16">
                            <read-more
                              class="mx-4 mt-1 custom-read-more font-weight-600 font-size-18 pb-1 custom-gray-color"
                              more-str="read more"
                              :text="
                                detail.description || '<em>No Description</em>'
                              "
                              link="#"
                              less-str="read less"
                              :max-chars="200"
                            >
                            </read-more>
                          </v-card-text>
                        </v-card>
                        <v-card
                          flat
                          class="custom-grey-border remove-border-radius mb-4"
                        >
                          <v-card-title
                            class="border-bottom gray-background py-1"
                          >
                            <span
                              class="font-weight-700 custom-headline color-custom-blue font-size-16"
                            >
                              TERMS &amp; CONDITIONS
                            </span>
                          </v-card-title>
                          <v-card-text class="p-6 font-size-16">
                            <div class="w-100 custom-grey-border p-4">
                              <read-more
                                class="mx-2 custom-read-more font-weight-600 font-size-18 pb-1 custom-gray-color"
                                more-str="read more"
                                :text="
                                  detail.term_conditions ||
                                  '<em>No Terms &amp; Conditions</em>'
                                "
                                link="#"
                                less-str="read less"
                                :max-chars="200"
                              >
                              </read-more>
                            </div>
                          </v-card-text>
                        </v-card>
                        <v-card
                          flat
                          class="custom-grey-border remove-border-radius mb-4"
                        >
                          <v-card-title
                            class="border-bottom gray-background py-1"
                          >
                            <span
                              class="font-weight-700 custom-headline color-custom-blue font-size-16 text-uppercase"
                            >
                              DELIVERY NOTE & REMARK
                            </span>
                          </v-card-title>
                          <v-card-text class="p-6 font-size-16">
                            <v-row>
                              <v-col cols="12" md="6">
                                <v-card-title
                                  class="border-bottom gray-background py-1"
                                >
                                  <span
                                    class="font-weight-700 custom-headline color-custom-blue font-size-16"
                                  >
                                    Delivery Note
                                  </span>
                                </v-card-title>
                                <read-more
                                  class="mx-4 mt-1 custom-read-more font-weight-600 font-size-18 pb-1 custom-gray-color"
                                  more-str="read more"
                                  :text="
                                    detail.delivery_note ||
                                    '<em>No delivery note</em>'
                                  "
                                  link="#"
                                  less-str="read less"
                                  :max-chars="200"
                                ></read-more>
                              </v-col>
                              <v-col cols="12" md="6">
                                <v-card-title
                                  class="border-bottom gray-background py-1"
                                >
                                  <span
                                    class="font-weight-700 custom-headline color-custom-blue font-size-16"
                                  >
                                    Remark
                                  </span>
                                </v-card-title>
                                <read-more
                                  class="mx-4 mt-1 custom-read-more font-weight-600 font-size-18 pb-1 custom-gray-color"
                                  more-str="read more"
                                  :text="detail.remark || '<em>No remark</em>'"
                                  link="#"
                                  less-str="read less"
                                  :max-chars="200"
                                ></read-more>
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>
                        <v-card
                          flat
                          class="custom-grey-border remove-border-radius mb-4"
                        >
                          <v-card-title
                            class="border-bottom gray-background py-1"
                          >
                            <span
                              class="font-weight-700 custom-headline color-custom-blue font-size-16 text-uppercase"
                            >
                              Internal Admin Note
                            </span>
                          </v-card-title>
                          <v-card-text class="p-6 font-size-16">
                            <div class="w-100 custom-grey-border p-4">
                              <read-more
                                class="mx-2 custom-read-more font-weight-600 font-size-18 pb-1 custom-gray-color"
                                more-str="read more"
                                :text="
                                  detail.admin_remark ||
                                  '<em>No internal admin note</em>'
                                "
                                link="#"
                                less-str="read less"
                                :max-chars="200"
                              ></read-more>
                            </div>
                            <FileTemplate
                              :attachments="detail.documents"
                              :allowDelete="canUpdatePurchaseOrder"
                            >
                            </FileTemplate>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </perfect-scrollbar>
                </v-tab-item>
                <v-tab-item value="line-item">
                  <PoLineItem
                    v-if="detail.id"
                    :key="lineItemKey"
                    isPurchaseOrder
                    type="purchase_order"
                    :detail="detail"
                    :pageLoading="pageLoading"
                    v-on:load:parent="getPurchaseOrder"
                    :canUpdate="canUpdatePurchaseOrder"
                    :related-id="detail.id"
                    :related-type="11"
                    :discount-level="
                      detail.discount_type == 2 ? 'line_item' : 'transaction'
                    "
                  ></PoLineItem>
                  <div class="mt-0 pr-2 font-weight-600">
                    <table width="100%">
                      <tr>
                        <td width="66%" class="py-1"></td>
                        <td width="18%" align="right" class="py-1">
                          Sub Total
                        </td>
                        <td width="18%" align="right" class="py-1">
                          {{ formatMoney(detail.sub_total) }}
                        </td>
                      </tr>
                      <tr v-if="detail.discount_type == 1">
                        <td class="py-1"></td>
                        <td align="right" class="py-1">
                          Discount
                          <span v-if="detail.discount_value_type == 1"
                            >({{ formatMoney(detail.discount_value) }})</span
                          >
                          <span v-if="detail.discount_value_type == 2"
                            >({{ formatNumber(detail.discount_value) }}%)</span
                          >
                        </td>
                        <td align="right" class="py-1">
                          {{ formatMoney(detail.discount_amount) }}
                        </td>
                      </tr>
                      <tr v-if="detail.tax_applied">
                        <td class="py-1"></td>
                        <td class="py-1" align="right">
                          Tax [GST {{ detail.tax_value }}%]
                        </td>
                        <td class="py-1" align="right">
                          {{ formatMoney(detail.tax_amount) }}
                        </td>
                      </tr>
                      <tr>
                        <td class="py-1"></td>
                        <td class="py-1" align="right">Adjustment</td>
                        <td class="py-1" align="right">
                          {{ formatMoney(detail.adjustment) }}
                        </td>
                      </tr>
                      <tr class="font-size-19">
                        <td class="py-1"></td>
                        <td class="py-1" align="right">Grand Total</td>
                        <td
                          class="py-1 font-weight-bold color-custom-blue"
                          align="right"
                        >
                          {{ formatMoney(detail.total) }}
                        </td>
                      </tr>
                    </table>
                  </div>
                </v-tab-item>
                <v-tab-item value="attachment">
                  <AdditionalDocuments
                    :type-id="detail.id"
                    class="mx-0 mb-3"
                    type-text="purchase-order"
                    :type="18"
                    :extensive="false"
                  ></AdditionalDocuments>
                </v-tab-item>
                <v-tab-item value="received-line-item">
                  <ReceivedLineItem
                    :key="receivedLineItemKey"
                    :purchaseOrder="purchaseOrder"
                  ></ReceivedLineItem>
                </v-tab-item>
                <v-tab-item value="invoice">
                  <POInvoice
                    :purchaseOrder="purchaseOrder"
                    :detail="detail"
                    :isApproved="isApprovedPO"
                    v-on:invoice:created="handleInvoiceCreated"
                    v-on:invoice:updated="handleInvoiceCreated"
                  >
                  </POInvoice>
                </v-tab-item>
                <v-tab-item value="history">
                  <InternalHistoryDetail
                    type="purchase-order"
                    :type_id="detail.id"
                  ></InternalHistoryDetail>
                </v-tab-item>
              </v-tabs-items>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </DetailTemplate>
    <SendMail
      :mail-dialog="mail_dialog"
      title="Send as Email"
      type="purchase-order"
      :type-id="purchaseOrder"
      v-on:close="mail_dialog = false"
    ></SendMail>

    <Dialog :commonDialog="receiveLineItemDialog" :dialogWidth="1500">
      <template v-slot:title>
        <div class="d-flex align-items-center w-100">
          <v-icon class="mr-2">mdi-package-down</v-icon>
          Receive Line Items
          <span v-if="detail && detail.barcode" class="ml-2">
            - {{ detail.barcode }}
          </span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            aria-label="Close receive line items dialog"
            v-on:click="closeReceiveLineItemDialog"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </template>
      <template v-slot:body>
        <v-container class="py-0" fluid>
          <PendingLineItem
            v-if="receiveLineItemDialog"
            ref="pendingLineItem"
            :purchaseOrder="purchaseOrder"
            v-on:reload:content="getPurchaseOrder"
            v-on:receipt:saved="handleReceiptSaved"
            v-on:selection:changed="pendingLineItemsSelected = $event"
          ></PendingLineItem>
        </v-container>
      </template>
      <template v-slot:action>
        <v-btn
          depressed
          :disabled="!pendingLineItemsSelected"
          class="px-6 custom-bold-button white--text mr-2"
          color="cyan darken-1"
          v-on:click="receivePendingLineItems"
        >
          <v-icon small left>mdi-check-circle-outline</v-icon>
          Save Received Line Items
        </v-btn>
        <v-btn
          depressed
          class="custom-grey-border custom-bold-button"
          v-on:click="closeReceiveLineItemDialog"
        >
          Close
        </v-btn>
      </template>
    </Dialog>

    <Dialog :commonDialog="statusDialog" :dialogWidth="650">
      <template v-slot:title>
        <div class="d-flex align-items-center w-100">
          <v-icon class="mr-2" color="primary">mdi-check-all</v-icon>
          Confirmation
          <v-spacer></v-spacer>
          <v-btn
            icon
            aria-label="Close status dialog"
            v-on:click="statusDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </template>
      <template v-slot:body>
        <v-container class="py-2" fluid>
          <p class="mb-0 text-center font-size-15 grey--text text--darken-2">
            Are you sure you want to change the status of
            <span class="font-weight-700 blue--text text--darken-2">
              Purchase Order #{{ detail?.barcode }}
            </span>
            to
            <span
              class="font-weight-700"
              :class="{
                'green--text': statusToUpdate == 2,
                'orange--text': statusToUpdate == 3,
                'red--text': statusToUpdate == 4,
              }"
            >
              {{ statusTextToUpdate }}
            </span>
            ?
          </p>
        </v-container>
      </template>
      <template v-slot:action>
        <v-btn
          depressed
          class="custom-grey-border custom-bold-button"
          v-on:click="statusDialog = false"
        >
          Cancel
        </v-btn>
        <v-btn
          depressed
          color="cyan"
          class="custom-bold-button white--text ml-2"
          :loading="statusLoading"
          :disabled="statusLoading"
          v-on:click="confirmStatusUpdate"
        >
          Confirm
        </v-btn>
      </template>
    </Dialog>

    <Dialog :commonDialog="approvalBlockedDialog" :dialogWidth="600">
      <template v-slot:title>
        <div class="d-flex align-items-center w-100">
          <v-icon class="mr-2" color="orange darken-2">mdi-alert</v-icon>
          Purchase Order Cannot Be Approved
          <v-spacer></v-spacer>
          <v-btn
            icon
            aria-label="Close approval warning"
            @click="approvalBlockedDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </template>
      <template v-slot:body>
        <p class="mb-0 text-center font-size-15 grey--text text--darken-2">
          The purchase order can only be approved when all linked quotations are
          accepted.
        </p>
      </template>
      <template v-slot:action>
        <v-btn
          depressed
          color="cyan"
          class="custom-bold-button white--text"
          @click="approvalBlockedDialog = false"
        >
          OK
        </v-btn>
      </template>
    </Dialog>
  </div>
</template>

<script>
import JwtService from "@/core/services/jwt.service";
import DetailTemplate from "@/view/pages/partials/Detail-Template.vue";
// import CustomStatus from "@/view/pages/partials/CustomStatus.vue";
// import LineItemDetail from "@/view/pages/partials/Detail/Line-Item-Detail.vue";
import InternalHistoryDetail from "@/view/pages/partials/Detail/Internal-History-Detail.vue";
import FileTemplate from "@/view/pages/partials/FileTemplate.vue";
import PendingLineItem from "@/view/pages/purchase-order/Detail/Pending-Line-Item";
import ReceivedLineItem from "@/view/pages/purchase-order/Detail/Received-Line-Item";
import CommonMixin from "@/core/plugins/common-mixin";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import { GET, PATCH } from "@/core/services/store/request.module";
//import LineItemOnlyView from "@/view/pages/partials/Line-Item-Only-View.vue";
import Barcode from "@/view/pages/partials/Barcode.vue";
import Dialog from "@/view/pages/partials/Dialog.vue";
import SendMail from "@/view/pages/partials/Send-Mail.vue";
import AdditionalDocuments from "@/view/pages/engineer/Additional-Documents";
import PoLineItem from "@/view/components/POLineItemDetails.vue";
import POInvoice from "@/view/pages/purchase-order/Detail/PO-Invoice.vue";

export default {
  mixins: [CommonMixin],
  data: () => {
    return {
      purchaseOrder: 0,
      pageLoading: true,
      detail: new Object(),
      moreActions: new Array(),
      mail_dialog: false,
      updateDetailDialog: false,
      purchaseOrderTab: "overview",
      receiveLineItemDialog: false,
      pendingLineItemsSelected: false,
      receivedLineItemKey: 0,
      lineItemKey: 0,
      statusDialog: false,
      approvalBlockedDialog: false,
      statusLoading: false,
      statusToUpdate: 0,
    };
  },
  methods: {
    getStatusColor(status) {
      if (status == 1) {
        return "cyan";
      }
      if (status == 2) {
        return "green lighten-1";
      }
      if (status == 3) {
        return "orange";
      }
      if (status == 4) {
        return "red";
      }
      if (status == 5) {
        return "orange";
      }
      if (status == 6) {
        return "green";
      }
      return null;
    },
    getPurchaseOrder() {
      const _this = this;
      _this.pageLoading = true;
      _this.$store
        .dispatch(GET, { url: "purchase-order/" + _this.purchaseOrder })
        .then(({ data }) => {
          _this.detail = {
            ...data,
            status: _this.lodash.toSafeInteger(data.status),
          };
          _this.moreActions = data.more_actions;
          _this.$store.dispatch(SET_BREADCRUMB, [
            {
              title: "Purchase Order",
              route: "purchase.order",
            },
            {
              title: "Detail",
            },
            {
              title: data.barcode,
            },
          ]);
        })
        .catch((error) => {
          _this.goBack();
          _this.logError(error);
        })
        .finally(() => {
          _this.pageLoading = false;
        });
    },
    handleInvoiceCreated() {
      this.getPurchaseOrder();
    },
    getPaymentStatusColor(status) {
      if (status === "paid") {
        return "green lighten-1";
      }
      if (status === "partially_paid") {
        return "orange darken-1";
      }
      if (status === "unpaid") {
        return "red lighten-1";
      }
      return "blue-grey";
    },
    updateMoreAction(action) {
      const _this = this;
      switch (action) {
        case "duplicate":
          _this.$router.push(
            _this.getDefaultRoute("purchase.order.create", {
              query: {
                duplicate: _this.detail.id,
              },
            })
          );
          break;
        case "edit":
          _this.$router.push(
            _this.getDefaultRoute("purchase.order.update", {
              params: {
                id: _this.detail.id,
              },
            })
          );
          break;
        case "mark_as_approved":
          _this.updateStatus("approved");
          break;
        case "mark_as_closed":
          _this.statusToUpdate = 3;
          _this.statusDialog = true;
          break;
        case "mark_as_cancel":
          _this.statusToUpdate = 4;
          _this.statusDialog = true;
          break;
        case "download_pdf":
          window.open(
            process.env.VUE_APP_API_URL +
              "purchase-order/" +
              _this.detail.id +
              "/pdf/download",
            "_blank"
          );
          break;
        case "print":
          window.open(
            process.env.VUE_APP_API_URL +
              "purchase-order/" +
              _this.detail.id +
              "/print",
            "_blank"
          );
          break;
        case "send_as_email":
          _this.mail_dialog = true;
          break;
      }
    },
    updateStatus(status) {
      if (status === "approved" && !this.areLinkedQuotationsAccepted()) {
        this.approvalBlockedDialog = true;
        return;
      }

      if (status === "approved") {
        this.statusToUpdate = 2;
        this.statusDialog = true;
      }
    },
    areLinkedQuotationsAccepted() {
      const lineItems = Array.isArray(this.detail.line_items)
        ? this.detail.line_items
        : [];
      const linkedQuotations = lineItems.filter(
        (lineItem) => this.lodash.toSafeInteger(lineItem.quotation_id) > 0
      );

      return linkedQuotations.every(
        (lineItem) => this.lodash.toSafeInteger(lineItem.quotation_status) === 2
      );
    },
    updatePurchaseOrderStatus(status) {
      const _this = this;
      _this.$store
        .dispatch(PATCH, {
          url: "purchase-order/" + _this.purchaseOrder,
          data: { status },
        })
        .then(() => {
          _this.getPurchaseOrder();
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.pageLoading = false;
        });
    },
    confirmStatusUpdate() {
      const _this = this;
      _this.statusLoading = true;
      _this.$store
        .dispatch(PATCH, {
          url: "purchase-order/" + _this.purchaseOrder,
          data: { status: _this.statusToUpdate },
        })
        .then(() => {
          _this.getPurchaseOrder();
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.statusLoading = false;
          _this.statusDialog = false;
        });
    },
    handleReceiptSaved() {
      this.pendingLineItemsSelected = false;
      this.receiveLineItemDialog = false;
      this.receivedLineItemKey += 1;
      this.lineItemKey += 1;
      this.purchaseOrderTab = "received-line-item";
      this.getPurchaseOrder();
    },
    receivePendingLineItems() {
      if (this.$refs.pendingLineItem) {
        this.$refs.pendingLineItem.receivePendingItem();
      }
    },
    closeReceiveLineItemDialog() {
      this.pendingLineItemsSelected = false;
      this.receiveLineItemDialog = false;
    },
  },
  components: {
    SendMail,
    //LineItemOnlyView,
    PendingLineItem,
    ReceivedLineItem,
    DetailTemplate,
    // CustomStatus,
    // LineItemDetail,
    InternalHistoryDetail,
    FileTemplate,
    AdditionalDocuments,
    PoLineItem,
    Dialog,
    Barcode,
    POInvoice,
  },
  created() {
    const _this = this;
    _this.purchaseOrder = _this.lodash.toSafeInteger(_this.$route.params.id);
    if (!_this.purchaseOrder || _this.purchaseOrder <= 0) {
      _this.$router.go(-1);
    }

    if (_this.$route.query && _this.$route.query.tab) {
      _this.purchaseOrderTab = _this.$route.query.tab;
    } else {
      _this.purchaseOrderTab = "overview";
    }
  },
  mounted() {
    const _this = this;
    _this.getPurchaseOrder();
    // _this.$store.dispatch(SET_BREADCRUMB, [
    //   {
    //     title: "Purchase Order",
    //     route: "purchase.order",
    //   },
    //   {
    //     title: "Detail",
    //   },
    // ]);
  },
  computed: {
    isPurchaseOrderPic() {
      const currentUser = JwtService.currentUser();
      return Boolean(
        currentUser &&
          this.detail &&
          this.detail.pic_user &&
          Number(this.detail.pic_user.id) === Number(currentUser.id)
      );
    },
    statusTextToUpdate() {
      if (this.statusToUpdate == 2) {
        return "Approved";
      }
      if (this.statusToUpdate == 3) {
        return "Closed";
      }
      if (this.statusToUpdate == 4) {
        return "Cancelled";
      }
      return "";
    },
    supplierAddress() {
      let result = new Array();
      if (this.detail && this.detail.supplier) {
        if (this.detail.supplier.unit_number) {
          result.push(this.detail.supplier.unit_number);
        }
        if (this.detail.supplier.address_line_1) {
          result.push(this.detail.supplier.address_line_1);
        }
        if (this.detail.supplier.address_line_2) {
          result.push(this.detail.supplier.address_line_2);
        }
        if (this.detail.supplier.postal_code) {
          result.push(this.detail.supplier.postal_code);
        }
      }
      return result.join(", ");
    },
    supplierEmail() {
      if (this.detail && this.detail.supplier) {
        if (this.detail.supplier.primary_email) {
          return this.detail.supplier.primary_email.value;
        }
      }
      return null;
    },
    supplierPhone() {
      if (this.detail && this.detail.supplier) {
        return this.detail.supplier.phone;
      }
      return null;
    },
    canUpdatePurchaseOrder() {
      if (this.detail && (this.detail.status == 3 || this.detail.status == 4)) {
        return false;
      }
      return this.getPermission("purchase-order:update");
    },
    canReceiveLineItems() {
      if (!this.detail || !this.detail.status) {
        return false;
      }
      if ([1, 3, 4, 6].includes(parseInt(this.detail.status))) {
        return false;
      }
      return true;
    },
    isApprovedPO() {
      if (!this.detail || !this.detail.status) {
        return false;
      }
      return [2, 3, 5, 6].includes(
        this.lodash.toSafeInteger(this.detail.status)
      );
    },
  },
};
</script>
